import { useEffect, useRef, useState } from 'react';

interface Props {
  /** True once the API reports the job is complete. Triggers jump to 100%. */
  done?: boolean;
  /** True if the job failed — bar stops, message switches to failure. */
  failed?: boolean;
  /** Optional error message to surface on failure. */
  errorMessage?: string;
}

// Phase messages crawl while the mock progress climbs to 90 % (matches WC timing).
const PHASES: Array<{ at: number; message: string }> = [
  { at: 0,  message: 'Sketching outlines…' },
  { at: 15, message: 'Mixing the palette…' },
  { at: 35, message: 'Painting the first layer…' },
  { at: 55, message: 'Still painting…' },
  { at: 75, message: 'Adding final touches…' },
];

const DURATION_MS = 80_000;   // 80 s to reach 90 %
const MOCK_CAP = 90;          // never auto-pass 90 — jump to 100 only when `done`
const TICK_MS = 250;

// Once real processing runs past the 80 s mock window, the bar itself must
// stop pretending to move — but the message keeps rotating so it never
// *looks* frozen (this is what customers were reading as "stuck at 1s" /
// reported as a network error even though the job was still running fine).
const OVERTIME_MESSAGES = [
  'Still painting — your portrait is in the final stretch ✨',
  'Almost there, adding the finishing touches…',
  'Great art takes a little extra time — hang tight…',
  'Still working — thanks for your patience!',
];
const OVERTIME_MESSAGE_MS = 6_000;

/**
 * ProgressBar — animates 0 → 90 % over 80 s while the API job runs (the bridge
 * worker typically completes in ~70 s). Jumps to 100 % when `done` flips true,
 * preserves last phase message on failure.
 */
const TOTAL_SECS = Math.round(DURATION_MS / 1000); // 80

export function ProgressBar({ done = false, failed = false, errorMessage }: Props) {
  const [percent, setPercent] = useState(0);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [secsLeft, setSecsLeft] = useState(TOTAL_SECS);
  const [overtime, setOvertime] = useState(false);
  const [overtimeIdx, setOvertimeIdx] = useState(0);
  const startedAt = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (failed) return;
    if (done) {
      setPercent(100);
      setSecsLeft(0);
      setOvertime(false);
      return;
    }

    startedAt.current = performance.now();
    const tick = () => {
      if (startedAt.current === null) return;
      const elapsed = performance.now() - startedAt.current;
      const next = Math.min(MOCK_CAP, (elapsed / DURATION_MS) * MOCK_CAP);
      setPercent(next);
      // Countdown 80 → 0 (never below 1 within the mock window).
      setSecsLeft(Math.max(1, Math.ceil((DURATION_MS - elapsed) / 1000)));
      setPhaseIdx(() => {
        let idx = 0;
        for (let i = 0; i < PHASES.length; i++) {
          if (next >= PHASES[i].at) idx = i;
        }
        return idx;
      });

      if (elapsed >= DURATION_MS) {
        setOvertime(true);
        setOvertimeIdx(Math.floor((elapsed - DURATION_MS) / OVERTIME_MESSAGE_MS) % OVERTIME_MESSAGES.length);
      }

      // Keep ticking past the mock cap (instead of stopping) so the overtime
      // message keeps rotating until the real API result (`done`/`failed`) arrives.
      rafRef.current = window.setTimeout(tick, TICK_MS) as unknown as number;
    };
    tick();
    return () => {
      if (rafRef.current !== null) clearTimeout(rafRef.current);
      startedAt.current = null;
    };
  }, [done, failed]);

  const label = failed
    ? errorMessage || 'Something went wrong. Please try again.'
    : done
      ? 'Your portrait is ready!'
      : overtime
        ? OVERTIME_MESSAGES[overtimeIdx]
        : PHASES[phaseIdx]?.message ?? 'Working…';

  return (
    <div className="w-full max-w-md mx-auto flex flex-col gap-3" aria-live="polite">
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(percent)}
        className="relative h-2 w-full bg-teal-dim rounded-full overflow-hidden"
      >
        <div
          className={[
            'absolute inset-y-0 left-0 rounded-full overflow-hidden transition-[width] duration-200 ease-linear',
            failed ? 'bg-red-500' : 'bg-teal',
          ].join(' ')}
          style={{ width: `${percent}%` }}
        >
          {!failed && !done && (
            <span
              aria-hidden="true"
              className="bf-bar-shimmer absolute inset-0 bg-gradient-to-r from-transparent via-white/55 to-transparent"
            />
          )}
        </div>
      </div>
      <p
        className={[
          'text-center text-sm font-medium',
          failed ? 'text-red-600' : 'text-black',
        ].join(' ')}
      >
        {label}
      </p>
      {!failed && !done && (
        overtime ? (
          <p className="bf-bar-overtime text-center text-xs text-mute">
            Still working<span className="bf-bar-dots" aria-hidden="true"><span>.</span><span>.</span><span>.</span></span>
          </p>
        ) : (
          <p className="text-center text-xs text-mute tabular-nums">
            ~<span className="font-semibold text-teal-dark">{secsLeft}s</span> remaining
          </p>
        )
      )}
    </div>
  );
}
